import { Subscription, fromEvent, merge, timer } from 'rxjs';

import { Injectable, inject, signal } from '@angular/core';

import { environment } from '@environments/environment';

import { UserStore } from '@cxc/core/services';
import {
  SessionChannel,
  SessionEvent,
} from '@services/session/session-channel.service';
import { SessionService } from '@services/session/session.service';

const INACTIVITY_MS = (environment.SESSION_ACTIVITY_MINUTES || 1) * 60 * 1000;
const GRACE_SECONDS = 60;

@Injectable({ providedIn: 'root' })
export class SessionInactivityService {
  private userStore = inject(UserStore);
  private session = inject(SessionService);
  // The tab's only session-control channel (LEN-2764), shared with
  // SessionService so a tab never hears its own LOGOUT.
  private channel = inject(SessionChannel);

  showWarningModal = signal(false);
  showSessionEndedModal = signal(false);
  countdown = signal(GRACE_SECONDS);

  private inactivityTimer?: Subscription;
  private graceTimer?: Subscription;
  private activityEvents?: Subscription;

  constructor() {
    this.listenBroadcast();
  }

  start(): void {
    this.stop();
    this.listenUserActivity();
    this.startInactivityTimer();
  }

  stop(): void {
    this.inactivityTimer?.unsubscribe();
    this.graceTimer?.unsubscribe();
    this.activityEvents?.unsubscribe();
  }

  continueSession(): void {
    this.broadcast({ type: 'SESSION_CONTINUE' });
    this.resetAll();
  }

  manualLogout(): void {
    this.endSession();
  }

  /**
   * Counts as user activity. Used for events the document listeners
   * cannot see, like interaction inside a cross-origin iframe.
   */
  registerActivity(): void {
    // Ignore activity while tracking is stopped (e.g. after logout)
    if (!this.activityEvents || this.activityEvents.closed) {
      return;
    }

    if (!this.showWarningModal()) {
      this.broadcast({ type: 'USER_ACTIVITY' });
      this.resetInactivityTimer();
    }
  }

  private listenUserActivity(): void {
    this.activityEvents = merge(
      fromEvent(document, 'click'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'touchstart')
    ).subscribe(() => this.registerActivity());
  }

  private startInactivityTimer(): void {
    this.inactivityTimer?.unsubscribe();
    this.inactivityTimer = timer(INACTIVITY_MS).subscribe(() => {
      this.startGracePeriod();
    });
  }

  private resetInactivityTimer(): void {
    this.startInactivityTimer();
  }

  private startGracePeriod(): void {
    this.showWarningModal.set(true);
    this.countdown.set(GRACE_SECONDS);

    this.graceTimer?.unsubscribe();
    this.graceTimer = timer(0, 1000).subscribe(sec => {
      const remaining = GRACE_SECONDS - sec;
      this.countdown.set(remaining);

      if (remaining <= 0) {
        this.endSession();
      }
    });
  }

  private resetAll(): void {
    this.graceTimer?.unsubscribe();
    this.showWarningModal.set(false);
    this.startInactivityTimer();
  }

  /**
   * This tab ends the session: SessionService revokes it in identity-api,
   * tells the other tabs and clears this one (LEN-2764).
   */
  private endSession(): void {
    this.stop();
    this.showWarningModal.set(false);
    this.showSessionEndedModal.set(true);

    void this.session.logout();
  }

  /** Another tab ended the session: only clear this one. */
  private forceLogout(): void {
    this.stop();
    this.showWarningModal.set(false);
    this.showSessionEndedModal.set(true);

    this.userStore.logOut();
  }

  private broadcast(event: SessionEvent): void {
    this.channel.post(event);
  }

  private listenBroadcast(): void {
    this.channel.listen(data => {
      switch (data.type) {
        case 'USER_ACTIVITY':
          if (!this.showWarningModal()) {
            this.resetInactivityTimer();
          }
          break;

        case 'SESSION_CONTINUE':
          this.resetAll();
          break;

        case 'LOGOUT':
          this.forceLogout();
          break;
      }
    });
  }

  finalizeFlow() {
    this.showWarningModal.set(false);
    this.showSessionEndedModal.set(false);
    this.countdown.set(GRACE_SECONDS);
  }
}
